import {parseMoment} from '@utils'
import Model from './Model'

export default class EstimateOld extends Model {
    get created_at_moment() {
        return parseMoment(this.created_at, 'ddd MMMM D, YYYY, h:mm A')
    }

    get updated_at_moment() {
        return parseMoment(this.updated_at, 'ddd MMMM D, YYYY, h:mm A')
    }

    get status() {
        if (this.secured) return 'secured'
        if (this.declined) return 'declined'
        if (this.sent) return 'sent'
        return 'draft'
    }

    static async all(packageId = null) {
        const params = {}
        if (packageId) params.package_id = packageId
        const {data} = await Http.get(route('api.v1.estimates.index'), {params})
        return this.make(data)
    }

    static async secured(packageId = null) {
        const params = {secured: true}
        if (packageId) params.package_id = packageId
        const {data} = await Http.get(route('api.v1.estimates.index'), {params})
        return this.make(data)
    }

    static async find(id) {
        const {data} = await Http.get(route('api.v1.estimates.show', id))
        const estimate = this.make(data)
        estimate.visitTree(node => node.key = node.id)
        return estimate
    }

    static async create(attributes) {
        const url = route('api.v1.estimates.store')
        const {data} = await Http.post(url, this.parseAttributes(attributes))
        return this.make(data)
    }

    async update(attributes = {}) {
        // Hack to ensure only 1 save is called at a time - debounce is > then round trip causing DB lockups
        if (this.updating) {
            this.queued = attributes;
            return;
        }
        this.updating = true;
        const url = route('api.v1.estimates.update', this.id)
        const payload = this.constructor.parseAttributes({...this.getFormAttributes(), ...attributes})
        const {data} = await Http.put(url, payload)
        this.fillPrimitiveValues(data)
        this.reconciliateIds(data)
        this.updating = false;
        if (this.queued !== false) {
            let attributes = this.queued;
            this.queued = false;
            await this.update(attributes)
        }
    }

    async snapshot() {
        const {data} = await Http.put(route('api.v1.estimates.snapshot', this.id))
        return data
    }

    async secure() {
        const {data} = await Http.put(route('api.v1.estimates.secure', this.id))
        this.fillPrimitiveValues(data)
    }

    async decline() {
        const {data} = await Http.put(route('api.v1.estimates.decline', this.id))
        this.fillPrimitiveValues(data)
    }

    async clearAcceptanceOrDeclineStatus() {
        const {data} = await Http.put(route('api.v1.estimates.clear-secure-status', this.id))
        this.fillPrimitiveValues(data)
    }

    async delete() {
        await Http.delete(route('api.v1.estimates.delete', this.id))
    }

    async copyToClassicQuote() {
        const {data} = await Http.put(route('api.v1.estimates.to-quote', this.id))
        return data
    }

    async copyToClassicMaterialsList() {
        const {data} = await Http.put(route('api.v1.estimates.materials-to-classic', this.id))
        return data
    }

    async listMaterials(contextType, contextId) {
        const params = {context_type: contextType, context_id: contextId}
        const {data} = await Http.get(route('api.v1.estimates.materials', this.id), {params})
        return data
    }

    async listMaterialsStructure(contextType, contextId) {
        const params = {context_type: contextType, context_id: contextId}
        const {data} = await Http.get(route('api.v1.estimates.materials-structure', this.id), {params})
        return data
    }


    static async rebuildMaterials(estimateId) {
        await Http.put(route('api.v1.estimates.materials.rebuild', [estimateId]))
    }

    async updateMaterial(estimateMaterialId, payload) {
        const {data} = await Http.put(route('api.v1.estimates.materials.change', [this.id, estimateMaterialId]), payload)
        return data
    }

    async duplicate(package_id = null) {
        const {data} = await Http.put(route('api.v1.estimates.duplicate', this.id), {package_id})
        return data
    }

    visitTree(fn) {
        this.sections.forEach((section, sectionIndex) => {
            section.tasks.forEach((task, taskIndex) => {
                task.items.forEach((item, itemIndex) => {
                    fn(item, itemIndex, 'item', task)
                })
                fn(task, taskIndex, 'task', section)
            })
            fn(section, sectionIndex, 'section', this)
        })
    }

    reconciliateIds(updatedEstimate) {
        const reconciliate = (currentElement, newElement) => {
            if (!newElement) return
            currentElement.id = newElement.id
        }

        this.sections.forEach((section, sectionIndex) => {
            const newSection = updatedEstimate.sections?.[sectionIndex]
            reconciliate(section, newSection)
            if (newSection) {
                section.tasks.forEach((task, taskIndex) => {
                    const newTask = newSection.tasks?.[taskIndex]
                    reconciliate(task, newTask)
                    if (newTask) {
                        task.items.forEach((item, itemIndex) => {
                            const newItem = newTask.items?.[itemIndex]
                            reconciliate(item, newItem)
                        })
                    }
                })
            }
        })
    }

    fillPrimitiveValues(data) {
        this.locked = data.locked
        this.secured = data.secured
        this.declined = data.declined
        this.accepted_response = data.accepted_response
        this.declined_response = data.declined_response
        this.revision = data.revision
        this.created_at = data.created_at
        this.created_by = data.created_by
        this.updated_at = data.updated_at
        this.updated_by = data.updated_by
    }
}
